import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import apiService from '../services/apiService';
import PeriodSelector from './PeriodSelector';
import './HydroUsageChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const HydroUsageChart = ({ location = 'ThePoint' }) => {
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyData, setDailyData] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  
  const now = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selected12Year, setSelected12Year] = useState(now.getFullYear() - 1);
  const [selected12Month, setSelected12Month] = useState(now.getMonth() + 1);

  const getDateRange = useCallback(() => {
    const today = new Date();
    let startDate, endDate;
    
    switch (selectedPeriod) {
      case 'current':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'select':
        startDate = new Date(selectedYear, selectedMonth - 1, 1);
        endDate = new Date(selectedYear, selectedMonth, 0);
        break;
      case '12months':
        startDate = new Date(selected12Year, selected12Month - 1, 1);
        endDate = new Date(selected12Year + 1, selected12Month - 1, 0);
        break;
      case 'all':
      default:
        return { startDate: null, endDate: null };
    }
    
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
  }, [selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { startDate, endDate } = getDateRange();
        
        const [dailyResponse, hourlyResponse] = await Promise.all([
          apiService.getHydroDaily(location, startDate, endDate),
          apiService.getHydroHourlyStats(location, startDate, endDate)
        ]);
        
        if (!dailyResponse.data || dailyResponse.data.length === 0) {
          setError('No hydro usage data available for selected period');
          setDailyData(null);
          setHourlyData(null);
          setSummary(null);
          return;
        }

        setSummary(dailyResponse.summary);

        // Daily chart data
        setDailyData({
          labels: dailyResponse.data.map(d => d.date),
          datasets: [
            {
              label: 'Total kWh',
              data: dailyResponse.data.map(d => parseFloat(d.totalKwh)),
              borderColor: 'rgb(6, 182, 212)',
              backgroundColor: 'rgba(6, 182, 212, 0.5)',
              tension: 0.3,
              pointRadius: dailyResponse.data.length > 60 ? 0 : 3,
              pointHoverRadius: 5,
            }
          ]
        });

        // Hourly pattern chart data
        if (hourlyResponse.data && hourlyResponse.data.length > 0) {
          const hourLabels = hourlyResponse.data.map(d => {
            const hour = d.hour;
            if (hour === 0) return '12 AM';
            if (hour === 12) return '12 PM';
            if (hour < 12) return `${hour} AM`;
            return `${hour - 12} PM`;
          });

          setHourlyData({
            labels: hourLabels,
            datasets: [
              {
                label: 'Maximum',
                data: hourlyResponse.data.map(d => parseFloat(d.maxKwh)),
                borderColor: 'rgb(239, 68, 68)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
              },
              {
                label: 'Average',
                data: hourlyResponse.data.map(d => parseFloat(d.avgKwh)),
                borderColor: 'rgb(6, 182, 212)',
                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
              },
              {
                label: 'Minimum',
                data: hourlyResponse.data.map(d => parseFloat(d.minKwh)),
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
              }
            ]
          });
        }
      } catch (err) {
        console.error('Failed to load hydro data:', err);
        setError(err.message || 'Failed to load hydro data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [location, getDateRange]);

  const dailyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#e2e8f0', usePointStyle: true, pointStyle: 'circle' }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(2)} kWh`
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#94a3b8', maxTicksLimit: 15 },
        grid: { color: 'rgba(148, 163, 184, 0.1)' }
      },
      y: {
        ticks: { color: '#94a3b8', callback: (value) => `${value.toFixed(1)} kWh` },
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        title: { display: true, text: 'Daily Consumption (kWh)', color: '#94a3b8' }
      }
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false }
  };

  const hourlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#e2e8f0', usePointStyle: true, pointStyle: 'circle' }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(4)} kWh`
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#94a3b8' },
        grid: { color: 'rgba(148, 163, 184, 0.1)' }
      },
      y: {
        ticks: { color: '#94a3b8', callback: (value) => `${value.toFixed(2)} kWh` },
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        title: { display: true, text: 'Consumption (kWh)', color: '#94a3b8' }
      }
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false }
  };

  return (
    <div className="hydro-chart-container">
      <h3 className="hydro-chart-title">Electricity Usage - {location}</h3>
      
      <PeriodSelector
        selectedPeriod={selectedPeriod}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        selected12Year={selected12Year}
        selected12Month={selected12Month}
        onPeriodChange={setSelectedPeriod}
        onYearChange={setSelectedYear}
        onMonthChange={setSelectedMonth}
        on12YearChange={setSelected12Year}
        on12MonthChange={setSelected12Month}
        disabled={loading}
      />

      <div className="hydro-tabs">
        <button
          className={`hydro-tab ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Daily Usage
        </button>
        <button
          className={`hydro-tab ${activeTab === 'hourly' ? 'active' : ''}`}
          onClick={() => setActiveTab('hourly')}
        >
          Hourly Pattern
        </button>
      </div>

      {loading && <div className="hydro-chart-loading">Loading hydro usage data...</div>}
      {error && <div className="hydro-chart-error">{error}</div>}

      {!loading && !error && (
        <>
          {summary && (
            <p className="hydro-chart-subtitle">
              {summary.totalDays} days: {summary.startDate} to {summary.endDate}
            </p>
          )}
          
          <div className="hydro-chart-wrapper">
            {activeTab === 'daily' && dailyData && (
              <Line data={dailyData} options={dailyOptions} />
            )}
            {activeTab === 'hourly' && hourlyData && (
              <Line data={hourlyData} options={hourlyOptions} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HydroUsageChart;
